import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Camera, Upload, RefreshCw, User, AlertCircle, CheckCircle2, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FotoPerfilObligatoria = ({ user, onFotoSubida, motivoRechazo }) => {
  // i18n zona cliente (interior CA · F2): namespace gestor
  const { t } = useTranslation('gestor');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Iniciar cámara
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setShowCamera(true);
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
      toast.error(t('foto.errCamara'));
    }
  };

  // Detener cámara
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  }, [stream]);

  // Capturar foto de la cámara
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setPreview(imageData);
      stopCamera();
    }
  };

  // Manejar selección de archivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('foto.errArchivo'));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('foto.errTamano'));
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Subir foto con progreso
  const handleUpload = async () => {
    if (!preview) {
      toast.error(t('foto.errSinFoto'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadComplete(false);
    
    try {
      // Simular progreso durante la subida
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 15;
        });
      }, 200);

      await axios.post(`${API}/cliente/foto-perfil`, {
        foto_base64: preview
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadComplete(true);
      
      // Mostrar OK por 2 segundos antes de continuar
      setTimeout(() => {
        toast.success(t('foto.exitoSubida'));
        if (onFotoSubida) {
          onFotoSubida();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error al subir foto:', error);
      toast.error(error.response?.data?.detail || t('foto.errSubida'));
      setUploading(false);
      setUploadProgress(0);
      setUploadComplete(false);
    }
  };

  // Resetear para tomar otra foto
  const resetPhoto = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-xl" data-testid="foto-obligatoria-card">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
            <Camera className="w-8 h-8 text-teal-600" />
          </div>
          <CardTitle className="text-2xl text-gray-800">{t('foto.titulo')}</CardTitle>
          <CardDescription className="text-base text-gray-600">
            {t('foto.desc')}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Mensaje de rechazo si existe */}
          {motivoRechazo && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">{t('foto.rechazada')}</p>
                <p className="text-sm text-red-600 mt-1">{motivoRechazo}</p>
              </div>
            </div>
          )}

          {/* Instrucciones */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-medium text-blue-800 mb-2">{t('foto.requisitos')}</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                {t('foto.req1')}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                {t('foto.req2')}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                {t('foto.req3')}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                {t('foto.req4')}
              </li>
            </ul>
          </div>

          {/* Vista previa o cámara */}
          <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300">
            {showCamera ? (
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                className="w-full h-full object-cover"
              />
            ) : preview ? (
              <img
                src={preview}
                alt={t('foto.vistaPrevia')}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                <User className="w-20 h-20 mb-2" />
                <p className="text-sm">{t('foto.apareceraAqui')}</p>
              </div>
            )}
          </div>

          {/* Canvas oculto para captura */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Botones de acción */}
          <div className="space-y-3">
            {!showCamera && !preview && (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={startCamera}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                  data-testid="btn-abrir-camara"
                >
                  <Camera className="w-4 h-4" />
                  {t('foto.tomarSelfie')}
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                  data-testid="btn-subir-archivo"
                >
                  <Upload className="w-4 h-4" />
                  {t('foto.subirFoto')}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            )}

            {showCamera && (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={stopCamera}
                  variant="outline"
                  className="w-full"
                >
                  {t('comun.cancelar')}
                </Button>
                <Button
                  onClick={capturePhoto}
                  className="w-full bg-teal-500 hover:bg-teal-600"
                  data-testid="btn-capturar"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {t('foto.capturar')}
                </Button>
              </div>
            )}

            {preview && !showCamera && (
              <div className="space-y-3">
                {/* Barra de progreso durante la subida */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {uploadComplete ? t('foto.subida') : t('foto.subiendo')}
                      </span>
                      <span className={`font-bold ${uploadComplete ? 'text-green-600' : 'text-teal-600'}`}>
                        {uploadComplete ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-5 h-5" />
                            OK!
                          </span>
                        ) : (
                          `${uploadProgress}%`
                        )}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${uploadComplete ? 'bg-green-500' : 'bg-teal-500'}`}
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    {uploadComplete && (
                      <p className="text-center text-green-600 font-medium text-sm">
                        {t('foto.perfecto')}
                      </p>
                    )}
                  </div>
                )}
                
                {!uploading && (
                  <>
                    <Button
                      onClick={handleUpload}
                      className="w-full bg-teal-500 hover:bg-teal-600"
                      disabled={uploading}
                      data-testid="btn-confirmar-foto"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('foto.confirmarEnviar')}
                    </Button>
                    <Button
                      onClick={resetPhoto}
                      variant="outline"
                      className="w-full"
                      disabled={uploading}
                      data-testid="btn-otra-foto"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('foto.otraFoto')}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mensaje de privacidad */}
          <p className="text-xs text-gray-500 text-center">
            {t('foto.privacidad')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FotoPerfilObligatoria;
